import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { isCoarsePointer, prefersReducedMotion } from "../utils/device";
import "./TargetCursor.css";

interface TargetCursorProps {
  targetSelector?: string;
  geometrySelector?: string;
  scopeSelector?: string;
  pauseSelector?: string;
  spinDuration?: number;
  hideDefaultCursor?: boolean;
  hoverDuration?: number;
  parallaxOn?: boolean;
  parallaxStrength?: number;
  idleOpacity?: number;
  idleScale?: number;
  activeScale?: number;
  cornerSize?: number;
  borderWidth?: number;
  dotSize?: number;
  targetPadding?: number;
  color?: string;
  freezeSelector?: string;
}

const bindHoverListeners = (
  selector: string | undefined,
  onEnter: () => void,
  onLeave: () => void
) => {
  const elements = selector
    ? Array.from(document.querySelectorAll<HTMLElement>(selector))
    : [];

  elements.forEach((element) => {
    element.addEventListener("mouseenter", onEnter);
    element.addEventListener("mouseleave", onLeave);
  });

  return () => {
    elements.forEach((element) => {
      element.removeEventListener("mouseenter", onEnter);
      element.removeEventListener("mouseleave", onLeave);
    });
  };
};

const TargetCursor = ({
  targetSelector = ".cursor-target",
  geometrySelector,
  scopeSelector,
  pauseSelector,
  spinDuration = 2,
  hideDefaultCursor = true,
  hoverDuration = 0.2,
  parallaxOn = true,
  parallaxStrength = 1,
  idleOpacity = 1,
  idleScale = 1,
  activeScale = 1,
  cornerSize = 12,
  borderWidth = 3,
  dotSize = 4,
  targetPadding = 3,
  color = "#ff0066",
  freezeSelector,
}: TargetCursorProps) => {
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const cornersRef = useRef<NodeListOf<HTMLDivElement> | null>(null);
  const spinTl = useRef<gsap.core.Timeline | null>(null);
  const dotRef = useRef<HTMLDivElement | null>(null);

  const targetCornerPositionsRef = useRef<
    Array<{ x: number; y: number }> | null
  >(null);
  const tickerFnRef = useRef<gsap.TickerCallback | null>(null);
  const activeStrengthRef = useRef({ current: 0 });
  const activeTargetRef = useRef<HTMLElement | null>(null);
  const currentLeaveHandlerRef = useRef<(() => void) | null>(null);
  const resumeTimeoutRef = useRef<number | null>(null);
  const isInsideScopeRef = useRef(false);
  const isPauseHoveredRef = useRef(false);
  const lockedCornerOffsetsRef = useRef<Array<{ x: number; y: number }> | null>(
    null
  );
  const wasGeometryFrozenRef = useRef(false);

  const isDisabled = useMemo(
    () => isCoarsePointer() || prefersReducedMotion(),
    []
  );

  const constants = useMemo(
    () => ({
      borderWidth,
      cornerSize,
      dotSize,
      targetPadding,
    }),
    [borderWidth, cornerSize, dotSize, targetPadding]
  );

  const resolveGeometryTarget = useCallback(
    (target: HTMLElement) => {
      if (!geometrySelector) return target;
      return (target.querySelector(geometrySelector) as HTMLElement | null) ?? target;
    },
    [geometrySelector]
  );

  const getTargetGeometry = useCallback(
    (target: HTMLElement) => {
      const geometryTarget = resolveGeometryTarget(target);
      const rect = geometryTarget.getBoundingClientRect();
      let transformSource: HTMLElement | null = geometryTarget;
      let matrix: DOMMatrixReadOnly | null = null;

      while (transformSource && transformSource !== document.body) {
        const transformValue = window.getComputedStyle(transformSource).transform;

        if (transformValue && transformValue !== "none" && window.DOMMatrixReadOnly) {
          matrix = new window.DOMMatrixReadOnly(transformValue);
          break;
        }

        transformSource = transformSource.parentElement;
      }

      const angle = matrix
        ? (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI
        : 0;
      const radians = (angle * Math.PI) / 180;
      const halfWidth = geometryTarget.offsetWidth / 2 + constants.targetPadding;
      const halfHeight = geometryTarget.offsetHeight / 2 + constants.targetPadding;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const rotatePoint = (x: number, y: number) => ({
        x: centerX + x * Math.cos(radians) - y * Math.sin(radians),
        y: centerY + x * Math.sin(radians) + y * Math.cos(radians),
      });

      return {
        angle,
        corners: [
          rotatePoint(-halfWidth, -halfHeight),
          rotatePoint(halfWidth, -halfHeight),
          rotatePoint(halfWidth, halfHeight),
          rotatePoint(-halfWidth, halfHeight),
        ],
      };
    },
    [constants.targetPadding, resolveGeometryTarget]
  );

  useEffect(() => {
    if (isDisabled || !cursorRef.current) return;

    const cursor = cursorRef.current;
    const activeStrength = activeStrengthRef.current;
    const originalBodyCursor = document.body.style.cursor;
    cornersRef.current = cursor.querySelectorAll(
      ".target-cursor-corner"
    ) as NodeListOf<HTMLDivElement>;
    const cornerElements = Array.from(cornersRef.current);

    if (hideDefaultCursor) {
      document.body.style.cursor = "none";
    }

    const setTargetCursorHidden = (target: HTMLElement | null, hidden: boolean) => {
      if (!target || hideDefaultCursor) return;

      if (hidden) {
        if (!target.dataset.targetCursorOriginalCursor) {
          target.dataset.targetCursorOriginalCursor =
            window.getComputedStyle(target).cursor || "auto";
        }
        target.style.cursor = "none";
      } else {
        target.style.cursor = target.dataset.targetCursorOriginalCursor || "";
        delete target.dataset.targetCursorOriginalCursor;
      }
    };

    const cleanupTarget = (target: HTMLElement | null) => {
      if (!target) return;

      setTargetCursorHidden(target, false);

      if (currentLeaveHandlerRef.current) {
        target.removeEventListener("mouseleave", currentLeaveHandlerRef.current);
      }

      currentLeaveHandlerRef.current = null;
    };

    gsap.set(cursor, {
      xPercent: -50,
      yPercent: -50,
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      opacity: scopeSelector ? 0 : idleOpacity,
      scale: idleScale,
    });

    const createSpinTimeline = () => {
      spinTl.current?.kill();
      spinTl.current = gsap
        .timeline({ repeat: -1 })
        .to(cursor, { rotation: "+=360", duration: spinDuration, ease: "none" });

      if (scopeSelector) {
        spinTl.current.pause(0);
      }
    };

    const pauseSpin = () => {
      gsap.killTweensOf(cursor, "rotation");
      spinTl.current?.pause();
    };

    const resumeSpin = () => {
      if (
        activeTargetRef.current ||
        isPauseHoveredRef.current ||
        (scopeSelector && !isInsideScopeRef.current)
      ) {
        return;
      }

      if (spinTl.current) {
        spinTl.current.resume();
      } else {
        createSpinTimeline();
      }
    };

    createSpinTimeline();

    // Geometry cache: targets can still be mid CSS transition when acquired
    // (e.g. hover scale), so re-measure per frame until two consecutive
    // frames agree, then serve from cache until something invalidates it
    // (scroll, resize, unfreeze, target change).
    let cachedGeometry: ReturnType<typeof getTargetGeometry> | null = null;
    let isGeometrySettled = false;

    const invalidateTargetGeometry = () => {
      cachedGeometry = null;
      isGeometrySettled = false;
    };

    const measureTargetGeometry = (target: HTMLElement) => {
      if (cachedGeometry && isGeometrySettled) {
        return cachedGeometry;
      }

      const fresh = getTargetGeometry(target);
      isGeometrySettled = Boolean(
        cachedGeometry &&
          cachedGeometry.angle === fresh.angle &&
          cachedGeometry.corners.every(
            (corner, index) =>
              corner.x === fresh.corners[index].x &&
              corner.y === fresh.corners[index].y
          )
      );
      cachedGeometry = fresh;
      return fresh;
    };

    // Persistent per-corner followers replace the four tweens the ticker
    // used to allocate on every frame. They are rebuilt only when the follow
    // duration regime changes or after the corner tweens are killed.
    type CornerFollower = {
      x: (value: number) => void;
      y: (value: number) => void;
      rotation: (value: number) => void;
    };

    let cornerFollowers: CornerFollower[] | null = null;
    let cornerFollowerDuration: number | null = null;
    let cornerFollowerTweens: gsap.core.Tween[] = [];

    const dropCornerFollowers = () => {
      cornerFollowerTweens.forEach((tween) => tween.kill());
      cornerFollowerTweens = [];
      cornerFollowers = null;
      cornerFollowerDuration = null;
    };

    const getCornerFollowers = (duration: number) => {
      if (cornerFollowers && cornerFollowerDuration === duration) {
        return cornerFollowers;
      }

      dropCornerFollowers();
      // Matches the old per-frame overwrite: "auto" on regime changes.
      gsap.killTweensOf(cornerElements, "x,y,rotation");

      if (duration === 0) {
        cornerFollowers = cornerElements.map((corner) => ({
          x: gsap.quickSetter(corner, "x", "px") as (value: number) => void,
          y: gsap.quickSetter(corner, "y", "px") as (value: number) => void,
          rotation: gsap.quickSetter(corner, "rotation", "deg") as (
            value: number
          ) => void,
        }));
      } else {
        cornerFollowers = cornerElements.map((corner) => {
          const x = gsap.quickTo(corner, "x", { duration, ease: "power1.out" });
          const y = gsap.quickTo(corner, "y", { duration, ease: "power1.out" });
          const rotation = gsap.quickTo(corner, "rotation", {
            duration,
            ease: "power1.out",
          });
          cornerFollowerTweens.push(x.tween, y.tween, rotation.tween);
          return { x, y, rotation };
        });
      }

      cornerFollowerDuration = duration;
      return cornerFollowers;
    };

    const tickerFn = () => {
      if (!activeTargetRef.current || !cursorRef.current || !cornersRef.current) {
        return;
      }

      const strength = activeStrength.current;
      if (strength === 0) return;

      const isFrozen = Boolean(
        freezeSelector && activeTargetRef.current.closest(freezeSelector)
      );

      if (isFrozen) {
        wasGeometryFrozenRef.current = true;
        return;
      }

      const justUnfroze = wasGeometryFrozenRef.current;
      if (justUnfroze) {
        wasGeometryFrozenRef.current = false;
        invalidateTargetGeometry();
      }

      const { cornerSize } = constants;
      const geometry = measureTargetGeometry(activeTargetRef.current);
      const cursorX = Number(gsap.getProperty(cursorRef.current, "x"));
      const cursorY = Number(gsap.getProperty(cursorRef.current, "y"));

      if (
        justUnfroze ||
        (parallaxOn &&
          parallaxStrength < 1 &&
          strength >= 0.99 &&
          !lockedCornerOffsetsRef.current)
      ) {
        lockedCornerOffsetsRef.current = cornerElements.map((cornerEl) => ({
          x: Number(gsap.getProperty(cornerEl, "x")),
          y: Number(gsap.getProperty(cornerEl, "y")),
        }));
      }

      const duration = justUnfroze
        ? 0.28
        : strength >= 0.99
          ? parallaxOn
            ? 0.08
            : 0
          : 0.05;
      const followers = getCornerFollowers(duration);

      cornerElements.forEach((corner, index) => {
        const currentX = Number(gsap.getProperty(corner, "x"));
        const currentY = Number(gsap.getProperty(corner, "y"));
        const idealX = geometry.corners[index].x - cornerSize / 2 - cursorX;
        const idealY = geometry.corners[index].y - cornerSize / 2 - cursorY;

        let targetX = idealX;
        let targetY = idealY;

        if (
          parallaxOn &&
          parallaxStrength < 1 &&
          lockedCornerOffsetsRef.current
        ) {
          const locked = lockedCornerOffsetsRef.current[index];
          targetX = locked.x + (idealX - locked.x) * parallaxStrength;
          targetY = locked.y + (idealY - locked.y) * parallaxStrength;
        }

        const follower = followers[index];
        follower.x(currentX + (targetX - currentX) * strength);
        follower.y(currentY + (targetY - currentY) * strength);
        follower.rotation(geometry.angle);
      });
    };

    tickerFnRef.current = tickerFn;

    const setX = gsap.quickTo(cursor, "x", {
      duration: 0.1,
      ease: "power3.out",
    });
    const setY = gsap.quickTo(cursor, "y", {
      duration: 0.1,
      ease: "power3.out",
    });
    const moveHandler = (event: MouseEvent) => {
      setX(event.clientX);
      setY(event.clientY);
    };
    window.addEventListener("mousemove", moveHandler);

    const scrollHandler = () => {
      invalidateTargetGeometry();

      if (!activeTargetRef.current || !cursorRef.current) return;

      const mouseX = Number(gsap.getProperty(cursorRef.current, "x"));
      const mouseY = Number(gsap.getProperty(cursorRef.current, "y"));
      const elementUnderMouse = document.elementFromPoint(mouseX, mouseY);
      const isStillOverTarget =
        elementUnderMouse &&
        (elementUnderMouse === activeTargetRef.current ||
          elementUnderMouse.closest(targetSelector) === activeTargetRef.current);

      if (!isStillOverTarget && currentLeaveHandlerRef.current) {
        currentLeaveHandlerRef.current();
      }
    };

    // Capture phase: the home page scrolls inside an inner container whose
    // scroll events never bubble to window.
    window.addEventListener("scroll", scrollHandler, {
      capture: true,
      passive: true,
    });

    const resizeHandler = () => invalidateTargetGeometry();
    window.addEventListener("resize", resizeHandler);

    const releaseActiveTarget = () => {
      gsap.ticker.remove(tickerFn);
      dropCornerFollowers();
      invalidateTargetGeometry();
      targetCornerPositionsRef.current = null;
      lockedCornerOffsetsRef.current = null;
      wasGeometryFrozenRef.current = false;
      gsap.set(activeStrength, { current: 0, overwrite: true });

      const previousTarget = activeTargetRef.current;
      activeTargetRef.current = null;

      gsap.killTweensOf(cornerElements);
      const { cornerSize } = constants;
      const positions = [
        { x: -cornerSize * 1.5, y: -cornerSize * 1.5 },
        { x: cornerSize * 0.5, y: -cornerSize * 1.5 },
        { x: cornerSize * 0.5, y: cornerSize * 0.5 },
        { x: -cornerSize * 1.5, y: cornerSize * 0.5 },
      ];

      cornerElements.forEach((corner, index) => {
        gsap.to(corner, {
          x: positions[index].x,
          y: positions[index].y,
          rotation: 0,
          duration: 0.3,
          ease: "power3.out",
        });
      });

      gsap.to(cursorRef.current, {
        opacity: scopeSelector ? (isInsideScopeRef.current ? idleOpacity : 0) : idleOpacity,
        scale: idleScale,
        duration: 0.18,
        ease: "power2.out",
        overwrite: "auto",
      });

      resumeTimeoutRef.current = window.setTimeout(() => {
        if (!activeTargetRef.current && cursorRef.current) {
          resumeSpin();
        }
        resumeTimeoutRef.current = null;
      }, 50);

      cleanupTarget(previousTarget);
    };

    const enterHandler = (event: MouseEvent) => {
      if (scopeSelector && !isInsideScopeRef.current) return;

      const directTarget = event.target as HTMLElement | null;
      const target = directTarget?.closest(targetSelector) as HTMLElement | null;

      if (!target || !cursorRef.current || !cornersRef.current) return;
      if (activeTargetRef.current === target) return;

      if (activeTargetRef.current) {
        cleanupTarget(activeTargetRef.current);
      }

      if (resumeTimeoutRef.current) {
        window.clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }

      activeTargetRef.current = target;
      setTargetCursorHidden(target, true);

      dropCornerFollowers();
      cornerElements.forEach((corner) => gsap.killTweensOf(corner));

      pauseSpin();
      gsap.set(cursorRef.current, { rotation: 0 });
      gsap.to(cursorRef.current, {
        opacity: 1,
        duration: hoverDuration,
        ease: "power2.out",
        overwrite: "auto",
      });

      const { cornerSize } = constants;
      const cursorX = Number(gsap.getProperty(cursorRef.current, "x"));
      const cursorY = Number(gsap.getProperty(cursorRef.current, "y"));
      const geometry = getTargetGeometry(target);
      cachedGeometry = geometry;
      isGeometrySettled = false;

      targetCornerPositionsRef.current = geometry.corners.map((corner) => ({
        x: corner.x - cornerSize / 2,
        y: corner.y - cornerSize / 2,
      }));

      gsap.ticker.add(tickerFn);
      gsap.to(activeStrength, {
        current: 1,
        duration: hoverDuration,
        ease: "power2.out",
      });

      cornerElements.forEach((corner, index) => {
        gsap.to(corner, {
          x: targetCornerPositionsRef.current![index].x - cursorX,
          y: targetCornerPositionsRef.current![index].y - cursorY,
          rotation: geometry.angle,
          duration: hoverDuration,
          ease: "power2.out",
        });
      });
      gsap.to(cursorRef.current, {
        opacity: 1,
        scale: activeScale,
        duration: hoverDuration,
        ease: "power2.out",
        overwrite: "auto",
      });

      currentLeaveHandlerRef.current = releaseActiveTarget;
      target.addEventListener("mouseleave", releaseActiveTarget);
    };

    window.addEventListener("mouseover", enterHandler, { passive: true });

    const handleScopeEnter = () => {
      isInsideScopeRef.current = true;
      resumeSpin();

      gsap.to(cursor, {
        opacity: idleOpacity,
        scale: idleScale,
        duration: 0.18,
        ease: "power2.out",
        overwrite: "auto",
      });
    };

    const handleScopeLeave = () => {
      isInsideScopeRef.current = false;
      pauseSpin();

      if (currentLeaveHandlerRef.current) {
        currentLeaveHandlerRef.current();
      } else {
        gsap.to(cursor, {
          opacity: 0,
          scale: idleScale,
          duration: 0.18,
          ease: "power2.out",
          overwrite: "auto",
        });
      }
    };

    const unbindScopeListeners = bindHoverListeners(
      scopeSelector,
      handleScopeEnter,
      handleScopeLeave
    );

    const handlePauseEnter = () => {
      isPauseHoveredRef.current = true;
      pauseSpin();
    };

    const handlePauseLeave = () => {
      isPauseHoveredRef.current = false;
      resumeSpin();
    };

    const unbindPauseListeners = bindHoverListeners(
      pauseSelector,
      handlePauseEnter,
      handlePauseLeave
    );

    return () => {
      if (tickerFnRef.current) {
        gsap.ticker.remove(tickerFnRef.current);
      }

      dropCornerFollowers();

      if (resumeTimeoutRef.current) {
        window.clearTimeout(resumeTimeoutRef.current);
      }

      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseover", enterHandler);
      window.removeEventListener("scroll", scrollHandler, { capture: true });
      window.removeEventListener("resize", resizeHandler);

      unbindScopeListeners();
      unbindPauseListeners();

      cleanupTarget(activeTargetRef.current);
      spinTl.current?.kill();
      document.body.style.cursor = originalBodyCursor;
      targetCornerPositionsRef.current = null;
      lockedCornerOffsetsRef.current = null;
      wasGeometryFrozenRef.current = false;
      activeStrength.current = 0;
      isInsideScopeRef.current = false;
      isPauseHoveredRef.current = false;
    };
  }, [
    targetSelector,
    geometrySelector,
    scopeSelector,
    pauseSelector,
    spinDuration,
    constants,
    getTargetGeometry,
    hideDefaultCursor,
    isDisabled,
    hoverDuration,
    parallaxOn,
    parallaxStrength,
    idleOpacity,
    idleScale,
    activeScale,
    freezeSelector,
  ]);

  useEffect(() => {
    if (isDisabled || !cursorRef.current || !spinTl.current) return;

    if (spinTl.current.isActive()) {
      spinTl.current.kill();
      spinTl.current = gsap
        .timeline({ repeat: -1 })
        .to(cursorRef.current, { rotation: "+=360", duration: spinDuration, ease: "none" });
    }
  }, [spinDuration, isDisabled]);

  if (isDisabled) {
    return null;
  }

  return (
    <div
      ref={cursorRef}
      className="target-cursor-wrapper"
      style={
        {
          "--target-cursor-corner-size": `${cornerSize}px`,
          "--target-cursor-border-width": `${borderWidth}px`,
          "--target-cursor-dot-size": `${dotSize}px`,
          "--target-cursor-color": color,
        } as CSSProperties
      }
    >
      <div ref={dotRef} className="target-cursor-dot" />
      <div className="target-cursor-corner corner-tl" />
      <div className="target-cursor-corner corner-tr" />
      <div className="target-cursor-corner corner-br" />
      <div className="target-cursor-corner corner-bl" />
    </div>
  );
};

export default TargetCursor;
